/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Spin,
  Modal,
  InputNumber,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getConfiguracionesDispersionBancaria,
  postConfiguracionDispersionBancaria,
  patchConfiguracionDispersionBancaria,
  deleteConfiguracionDispersionBancaria,
  getConfiguracionDispersionBancaria,
  autorizarConfiguracionDispersionBancaria,
  cancelarConfiguracionDispersionBancaria,
  reactivarConfiguracionDispersionBancaria,
} from 'api/nominas/configuracionDispersionBancaria';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';

export const configuracionDispersionBancariaPermission = {
  permissionModel: 'configuraciondispersionbancaria',
  catalogsModels: [],
  tabsModels: [],
};

const ConfiguracionDispersionBancaria = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguracionesDispersionBancaria();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();

      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getConfiguracionesDispersionBancaria(),
      ]);

      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _config = await getConfiguracionDispersionBancaria(match.id);

    setTimeout(() => {
      if (_config) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_config));
          setDisableFields(_config?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });

    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      const saved = await (key
        ? patchConfiguracionDispersionBancaria(key, values)
        : postConfiguracionDispersionBancaria(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteConfiguracionDispersionBancaria(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const saved = await autorizarConfiguracionDispersionBancaria(key);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const saved = await cancelarConfiguracionDispersionBancaria(key);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const saved = await reactivarConfiguracionDispersionBancaria(key);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    }
  };

  const columns = [
    {
      titleText: 'Emisora',
      dataIndex: 'emisora',
      key: 'emisora',
      width: 120,
    },
    {
      titleText: 'Cuenta de Cargo',
      dataIndex: 'cuenta_cargo',
      key: 'cuenta_cargo',
      width: 160,
    },
    {
      titleText: 'Clave SPEI',
      dataIndex: 'clave_spei',
      key: 'clave_spei',
      width: 110,
    },
    {
      titleText: 'Consecutivo',
      dataIndex: 'consecutivo_default',
      key: 'consecutivo_default',
      width: 120,
    },
    {
      titleText: 'Clave de Servicio',
      dataIndex: 'clave_servicio',
      key: 'clave_servicio',
      width: 150,
    },
    {
      titleText: 'Tipo de Pago',
      dataIndex: 'tipo_pago',
      key: 'tipo_pago',
      width: 120,
    },
    {
      titleText: 'Leyenda Default',
      dataIndex: 'leyenda_default',
      key: 'leyenda_default',
      width: 200,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 130,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    emisora: [
      requiredRule,
      { len: 5, message: 'Debe ser exactamente 5 caracteres' },
      { pattern: /^\d+$/, message: 'Solo se permiten digitos numericos' },
    ],
    cuenta_cargo: [
      requiredRule,
      { len: 10, message: 'Debe ser exactamente 10 caracteres' },
      { pattern: /^\d+$/, message: 'Solo se permiten digitos numericos' },
    ],
    clave_spei: [
      requiredRule,
      { len: 3, message: 'Debe ser exactamente 3 caracteres' },
      { pattern: /^\d+$/, message: 'Solo se permiten digitos numericos' },
    ],
    consecutivo: [
      requiredRule,
      {
        type: 'number',
        min: 1,
        max: 99,
        message: 'Debe estar entre 1 y 99',
      },
    ],
    clave_servicio: [
      requiredRule,
      { len: 2, message: 'Debe ser exactamente 2 caracteres' },
    ],
    tipo_pago: [
      requiredRule,
      { len: 2, message: 'Debe ser exactamente 2 caracteres' },
    ],
  };

  const customActions = [
    {
      onClick: () => setVisibleAutorizar(true),
      permission: { model: 'configuraciondispersionbancaria', action: 'autorizar' },
      text: 'Autorizar',
      icon: <CheckCircleOutlined />,
      disabledBySelection: true,
      disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
      className: 'success',
    },
    {
      onClick: () => setVisibleCancelar(true),
      permission: { model: 'configuraciondispersionbancaria', action: 'cancelar' },
      text: 'Cancelar',
      icon: <CloseCircleOutlined />,
      disabledBySelection: true,
      disabled: selectedData?.estados_globales === ESTATUS_CANCELADO,
      className: 'cancel',
    },
    {
      onClick: () => setVisibleReactivar(true),
      permission: { model: 'configuraciondispersionbancaria', action: 'reactivar' },
      text: 'Reactivar',
      icon: <RedoOutlined />,
      disabledBySelection: true,
      disabled: selectedData?.estados_globales === ESTATUS_ACTIVO,
      className: 'success',
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={customActions}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={700}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Configuracion de Dispersion Bancaria`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="configuracion_dispersion_bancaria"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
              clave_spei: '072',
              consecutivo_default: 1,
              clave_servicio: 'NE',
              tipo_pago: '00',
            }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="emisora"
                  rules={rules.emisora}
                  label="Emisora"
                  hasFeedback
                  tooltip="Codigo de emisora Banorte (5 digitos)"
                >
                  <Input
                    maxLength={5}
                    disabled={disableFields}
                    placeholder="Ej. 83619"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="cuenta_cargo"
                  rules={rules.cuenta_cargo}
                  label="Cuenta de Cargo"
                  hasFeedback
                  tooltip="Cuenta de cargo Banorte (10 digitos)"
                >
                  <Input
                    maxLength={10}
                    disabled={disableFields}
                    placeholder="Ej. 1266129409"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  name="clave_spei"
                  rules={rules.clave_spei}
                  label="Clave SPEI"
                  hasFeedback
                  tooltip="Codigo SPEI del banco emisor (3 digitos, ej: 072 = Banorte)"
                >
                  <Input
                    maxLength={3}
                    disabled={disableFields}
                    placeholder="072"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  name="consecutivo_default"
                  rules={rules.consecutivo}
                  label="Consecutivo Default"
                  hasFeedback
                  tooltip="Consecutivo por defecto del dia (01-99)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    max={99}
                    disabled={disableFields}
                    placeholder="1"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estado"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  name="clave_servicio"
                  rules={rules.clave_servicio}
                  label="Clave de Servicio"
                  hasFeedback
                  tooltip="Clave de servicio Banorte para el header del archivo PAG (2 caracteres, ej: NE)"
                >
                  <Input
                    maxLength={2}
                    disabled={disableFields}
                    placeholder="NE"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  name="tipo_pago"
                  rules={rules.tipo_pago}
                  label="Tipo de Pago"
                  hasFeedback
                  tooltip="Tipo de pago para el header del archivo PAG (2 caracteres, ej: 00)"
                >
                  <Input
                    maxLength={2}
                    disabled={disableFields}
                    placeholder="00"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="leyenda_default"
                  label="Leyenda Default"
                  hasFeedback
                  tooltip="Texto por defecto para Referencia Leyenda Ordenante (max 40 caracteres)"
                >
                  <Input
                    maxLength={40}
                    disabled={disableFields}
                    placeholder="Ej. PAGO DE NOMINA"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Configuracion seleccionada"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Configuracion: ${selectedData?.emisora}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`Cancelar la Configuracion: ${selectedData?.emisora}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar la Configuracion: ${selectedData?.emisora}`}
          open={visibleReactivar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfiguracionDispersionBancaria;
